import React, { useContext, useState } from 'react';
import {
  Appbar, IconButton, List, Button, Chip, ActivityIndicator,
} from 'react-native-paper';
import { useMutation, useQuery } from '@apollo/client';
import {
  ScrollView, View, HStack, Text, VStack,
} from 'native-base';
import { useToast } from 'react-native-toast-notifications';
import { format } from 'date-fns';
import * as ImagePicker from 'expo-image-picker';
import { Linking, Platform, Share } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { FindManyTrips } from '../../gql/trip/query';
import RefreshableScreen from '../Refreshable/RefreshableScreen';
import LoadingIndicator from '../../components/commons/LoadingIndicator';
import { DELETE_TRIP, START_TRIP, END_TRIP, ADD_TRIP_PHOTO, REMOVE_TRIP_PHOTO, GENERATE_TRIP_SHARE_TOKEN } from '../../gql/trip/mutation';
import { useLocationTrackingContext } from '../../context/LocationTrackingContext';
import { AuthContext } from '../../context/AuthContext';
import { GOOGLE_PLAY_REVIEWER_EMAIL } from '../../utils/constant';
import TripPhotoGallery from '../../components/trip/TripPhotoGallery';

const STATUS_COLORS = {
  SCHEDULED: '#3b82f6',
  IN_PROGRESS: '#f97316',
  COMPLETED: '#22c55e',
  CANCELLED: '#ef4444',
};

const STATUS_LABELS = {
  SCHEDULED: 'Planifié',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

function ManageTrips() {
  const toast = useToast();
  const [expandedTripId, setExpandedTripId] = useState(null);
  const { authState } = useContext(AuthContext);
  const canTestLocationSharing = authState?.email?.toLowerCase() === GOOGLE_PLAY_REVIEWER_EMAIL;

  const { data, loading, refetch } = useQuery(FindManyTrips);

  const {
    location,
    errorMsg,
    isPermissionGranted,
    activeTrip,
    autoCompleted,
    requestPermissions,
  } = useLocationTrackingContext();

  const [isTestingLocationSharing, setIsTestingLocationSharing] = useState(false);
  const handleTestLocationSharing = async () => {
    setIsTestingLocationSharing(true);
    try {
      const granted = await requestPermissions();
      toast.show(granted
        ? 'Partage de position autorisé'
        : 'Partage de position refusé');
    } finally {
      setIsTestingLocationSharing(false);
    }
  };

  const [deleteTrip, { error: deleteError }] = useMutation(DELETE_TRIP, {
    onCompleted: () => {
      toast.show('Trip deleted successfully');
      refetch();
    },
    onError: (error) => {
      toast.show(error.message);
    },
  });

  const [startTrip] = useMutation(START_TRIP, {
    onCompleted: () => {
      toast.show('Trajet démarré - Suivi GPS activé');
      refetch();
    },
    onError: (error) => {
      toast.show(error.message);
    },
  });

  const [endTrip] = useMutation(END_TRIP, {
    onCompleted: () => {
      toast.show('Trajet terminé - Suivi GPS arrêté');
      refetch();
    },
    onError: (error) => {
      toast.show(error.message);
    },
  });

  const [addTripPhoto] = useMutation(ADD_TRIP_PHOTO, {
    onCompleted: () => refetch(),
    onError: (error) => toast.show(error.message),
  });

  const [removeTripPhoto] = useMutation(REMOVE_TRIP_PHOTO, {
    onCompleted: () => refetch(),
    onError: (error) => toast.show(error.message),
  });

  const [sharingTripId, setSharingTripId] = useState(null);
  const [copyingTripId, setCopyingTripId] = useState(null);
  const [generateTripShareToken] = useMutation(GENERATE_TRIP_SHARE_TOKEN, {
    onError: () => toast.show('Impossible de générer le lien de partage'),
  });

  const getTripShareUrl = async (tripId) => {
    const result = await generateTripShareToken({ variables: { tripId: Number(tripId) } });
    return result?.data?.generateTripShareToken?.shareUrl;
  };

  const handleShareTrip = async (tripId) => {
    setSharingTripId(tripId);
    try {
      const shareUrl = await getTripShareUrl(tripId);
      if (!shareUrl) return;
      const message = `Suivez mon trajet en direct : ${shareUrl}`;
      const whatsappUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
      const canOpen = await Linking.canOpenURL(whatsappUrl);
      if (canOpen) {
        await Linking.openURL(whatsappUrl);
      } else {
        await Share.share({ message });
      }
    } finally {
      setSharingTripId(null);
    }
  };

  const handleCopyTripLink = async (tripId) => {
    setCopyingTripId(tripId);
    try {
      const shareUrl = await getTripShareUrl(tripId);
      if (!shareUrl) return;
      await Clipboard.setStringAsync(shareUrl);
      toast.show('Lien copié dans le presse-papiers');
    } finally {
      setCopyingTripId(null);
    }
  };

  if (deleteError) toast.show(deleteError.message);

  const handleSlotPress = async (trip, phase, order) => {
    const existingPhoto = trip.photos?.find((p) => p.phase === phase && p.order === order);

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.7,
      base64: true,
    });

    if (!result.canceled) {
      const asset = result.assets[0];
      const photo = Platform.OS === 'web'
        ? asset.uri
        : `data:${asset.mimeType};base64,${asset.base64}`;
      addTripPhoto({
        variables: {
          tripId: Number(trip.id),
          phase,
          order,
          photo,
        },
      });
    }
  };

  const beforePhotoCount = (trip) => (trip.photos || []).filter((p) => p.phase === 'BEFORE').length;
  const afterPhotoCount = (trip) => (trip.photos || []).filter((p) => p.phase === 'AFTER').length;

  const renderTripActions = (trip) => {
    if (trip.status === 'SCHEDULED') {
      const hasBeforePhoto = beforePhotoCount(trip) > 0;
      return (
        <Button
          mode="contained"
          compact
          disabled={!hasBeforePhoto}
          onPress={() => startTrip({ variables: { id: Number(trip.id) } })}
        >
          Démarrer
        </Button>
      );
    }
    if (trip.status === 'IN_PROGRESS') {
      const hasAfterPhoto = afterPhotoCount(trip) > 0;
      return (
        <HStack space={1} alignItems="center">
          {sharingTripId === trip.id ? (
            <ActivityIndicator size="small" />
          ) : (
            <IconButton
              icon="whatsapp"
              size={20}
              iconColor="#25D366"
              onPress={() => handleShareTrip(trip.id)}
            />
          )}
          {copyingTripId === trip.id ? (
            <ActivityIndicator size="small" />
          ) : (
            <IconButton
              icon="content-copy"
              size={20}
              onPress={() => handleCopyTripLink(trip.id)}
            />
          )}
          <Button
            mode="contained"
            compact
            buttonColor="#22c55e"
            disabled={!hasAfterPhoto}
            onPress={() => endTrip({ variables: { id: Number(trip.id) } })}
          >
            Terminer
          </Button>
        </HStack>
      );
    }
    return null;
  };

  const renderPhotoSection = (trip) => {
    if (trip.status !== 'SCHEDULED' && trip.status !== 'IN_PROGRESS') return null;

    const editablePhase = trip.status === 'SCHEDULED' ? 'BEFORE' : 'AFTER';
    const activeCount = (trip.photos || []).filter((p) => p.phase === editablePhase).length;

    return (
      <VStack mt={2} space={1}>
        <TripPhotoGallery
          photos={trip.photos || []}
          editablePhase={editablePhase}
          onSlotPress={(phase, order) => handleSlotPress(trip, phase, order)}
          onRemovePhoto={(id) => removeTripPhoto({ variables: { id } })}
        />
        {activeCount === 0 && (
          <Text fontSize="xs" color="orange.500">
            {editablePhase === 'BEFORE'
              ? 'Prenez au moins 1 photo pour démarrer le trajet'
              : 'Prenez au moins 1 photo pour terminer le trajet'}
          </Text>
        )}
      </VStack>
    );
  };

  return (
    <>
      <Appbar.Header statusBarHeight={60}>
        <View style={{ flexDirection: 'column', width: '100%' }}>
          <View style={{
            flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16,
          }}
          >
            <Appbar.Content title="Mes trajets" />
            {canTestLocationSharing && (
              isTestingLocationSharing ? (
                <ActivityIndicator size="small" style={{ marginRight: 12 }} />
              ) : (
                <IconButton
                  icon="crosshairs-gps"
                  onPress={handleTestLocationSharing}
                  accessibilityLabel="Tester le partage de position"
                />
              )
            )}
          </View>
        </View>
      </Appbar.Header>

      {autoCompleted && (
        <View style={{ backgroundColor: '#22c55e', padding: 8 }}>
          <HStack justifyContent="center" alignItems="center" space={2}>
            <Text color="white" fontSize="sm" fontWeight="bold">
              ✓ Le trajet a été terminé automatiquement
            </Text>
          </HStack>
        </View>
      )}
      {!autoCompleted && activeTrip && (
        <View style={{ backgroundColor: '#f97316', padding: 8 }}>
          <HStack justifyContent="center" alignItems="center" space={2}>
            <Text color="white" fontSize="sm" fontWeight="bold">
              {isPermissionGranted && location
                ? 'GPS actif - Position envoyée'
                : 'Activation du GPS...'}
            </Text>
            {errorMsg && <Text color="white" fontSize="xs">{errorMsg}</Text>}
          </HStack>
        </View>
      )}

      <RefreshableScreen onRefresh={async () => { await refetch(); }}>
        {loading && <LoadingIndicator text="Chargement des trajets..." />}

        <ScrollView>
          {data?.myTripsAsDriver?.map((d) => (
            <List.Item
              key={d.id}
              title={`${d.departureLocation} → ${d.arrivalLocation}`}
              description={() => (
                <VStack>
                  <List.Subheader style={{ paddingLeft: 0, marginTop: -8 }}>
                    {`${format(new Date(d.departureDateTime), 'dd/MM/yyyy HH:mm')} - ${d.event.name}`}
                  </List.Subheader>
                  <Chip
                    compact
                    style={{ backgroundColor: STATUS_COLORS[d.status], alignSelf: 'flex-start' }}
                    textStyle={{ color: 'white', fontSize: 10 }}
                  >
                    {STATUS_LABELS[d.status]}
                  </Chip>
                  {renderPhotoSection(d)}
                </VStack>
              )}
              right={(props) => (
                <HStack space={2} alignItems="flex-start">
                  {renderTripActions(d)}
                  <IconButton
                    {...props}
                    icon="delete"
                    onPress={() => deleteTrip({ variables: { id: Number(d.id) } })}
                  />
                </HStack>
              )}
            />
          ))}
          {!loading && data?.myTripsAsDriver?.length === 0 && (
            <View style={{ padding: 16, alignItems: 'center' }}>
              <List.Item title="Aucun trajet assigné" />
            </View>
          )}
        </ScrollView>
      </RefreshableScreen>
    </>
  );
}

export default ManageTrips;
